import React from 'react';
import { ArrowDownRight, ArrowUpRight, Loader2, ShieldCheck } from 'lucide-react';
import { LevelControl } from './LevelControl';
import { lotForRisk, money, pipSize, pipsBetween, roundTo, validateStop } from './simMath';

export const OrderTicket = ({ pair, direction, onDirection, quote, levels, setLevels, risk, setRisk, onActivate, submitting }) => {
  const price = quote?.price;
  const digits = quote?.digits || 5;
  const ps = pipSize(pair);
  const sign = direction === 'BUY' ? 1 : -1;
  const { entry, sl, tp } = levels || {};
  const slPips = entry && sl ? pipsBetween(entry, sl, pair) : 0;
  const tpPips = entry && tp ? pipsBetween(entry, tp, pair) : 0;
  const entryPips = entry && price ? sign * (entry - price) / ps : 0;
  const lot = lotForRisk(Number(risk), slPips, quote?.pip_value_per_lot);
  const actualRisk = lot * slPips * (quote?.pip_value_per_lot || 0);
  const reward = lot * tpPips * (quote?.pip_value_per_lot || 0);
  const error = validateStop(direction, price, entry, sl, tp) || (!(Number(risk) > 0) ? 'Enter how much you want to risk' : null);

  const setEntryPips = (p) => {
    const e = roundTo(price + sign * p * ps, digits);
    setLevels({ entry: e, sl: roundTo(e - sign * slPips * ps, digits), tp: roundTo(e + sign * tpPips * ps, digits) });
  };
  const setDist = (k, p) => setLevels({ ...levels, [k]: roundTo(entry + (k === 'tp' ? sign : -sign) * p * ps, digits) });
  const nudge = (k, d) => (k === 'entry'
    ? setLevels({ entry: roundTo(entry + d * ps, digits), sl: roundTo(sl + d * ps, digits), tp: roundTo(tp + d * ps, digits) })
    : setLevels({ ...levels, [k]: roundTo(levels[k] + d * ps, digits) }));

  return (
    <div className="sim-panel flex flex-col gap-5 p-5" data-testid="order-ticket">
      <div className="grid grid-cols-2 gap-2" role="tablist">
        {['BUY', 'SELL'].map((d) => (
          <button key={d} type="button" onClick={() => onDirection(d)} data-testid={`direction-${d.toLowerCase()}-btn`}
            className={`sim-dir ${direction === d ? (d === 'BUY' ? 'sim-dir-buy' : 'sim-dir-sell') : ''}`}>
            {d === 'BUY' ? <ArrowUpRight size={16} /> : <ArrowDownRight size={16} />}
            {d} STOP
          </button>
        ))}
      </div>
      <p className="-mt-2 text-xs leading-relaxed text-slate-400" data-testid="order-type-help">
        {direction === 'BUY' ? 'Your entry sits ABOVE the current price and triggers when price rises to it.' : 'Your entry sits BELOW the current price and triggers when price falls to it.'}
      </p>

      <label className="block">
        <span className="text-xs font-semibold uppercase tracking-[0.14em] text-slate-400">Risk per trade</span>
        <div className="sim-risk mt-2">
          <span className="text-lg text-slate-400">$</span>
          <input type="number" min="1" inputMode="decimal" value={risk} onChange={(e) => setRisk(e.target.value)}
            className="sim-mono w-full bg-transparent text-2xl font-semibold text-white outline-none" data-testid="risk-amount-input" />
        </div>
        <span className="mt-1.5 block text-[11px] text-slate-500">Stays fixed — the lot size adjusts as you move the lines.</span>
      </label>

      <LevelControl id="entry" label="Entry" color="#38bdf8" price={entry} digits={digits} pips={entryPips} min={0.5} max={40}
        onPips={setEntryPips} onNudge={(d) => nudge('entry', d)} hint={`${direction === 'BUY' ? 'Above' : 'Below'} live price`} />
      <LevelControl id="sl" label="Stop Loss" color="#f43f5e" price={sl} digits={digits} pips={slPips} min={3} max={100}
        onPips={(p) => setDist('sl', p)} onNudge={(d) => nudge('sl', d)} hint="Distance from entry" />
      <LevelControl id="tp" label="Take Profit" color="#22c55e" price={tp} digits={digits} pips={tpPips} min={3} max={200}
        onPips={(p) => setDist('tp', p)} onNudge={(d) => nudge('tp', d)} hint="Distance from entry" />

      <div className="grid grid-cols-2 gap-px overflow-hidden rounded-xl bg-white/5" data-testid="order-summary">
        <Stat label="Lot size" value={lot.toFixed(2)} big testid="summary-lot-size" />
        <Stat label="Risk : Reward" value={slPips ? `1 : ${(tpPips / slPips).toFixed(2)}` : '—'} testid="summary-rr" />
        <Stat label="If SL hits" value={`-${money(actualRisk)}`} tone="text-rose-400" testid="summary-loss" />
        <Stat label="If TP hits" value={`+${money(reward)}`} tone="text-emerald-400" testid="summary-profit" />
      </div>

      {error && <div className="sim-alert" data-testid="order-validation-error">{error}</div>}

      <button type="button" disabled={!!error || submitting} onClick={() => onActivate({ lot })} className={`sim-activate ${direction === 'SELL' ? 'sim-activate-sell' : ''}`} data-testid="activate-order-btn">
        {submitting ? <Loader2 size={18} className="animate-spin" /> : <ShieldCheck size={18} />}
        {submitting ? 'Placing order…' : `ACTIVATE ${direction} STOP`}
      </button>
    </div>
  );
};

const Stat = ({ label, value, big, tone = 'text-white', testid }) => (
  <div className="bg-[#0d1524] px-4 py-3">
    <div className="text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-500">{label}</div>
    <div className={`sim-mono mt-1 font-semibold ${big ? 'text-2xl text-sky-300' : 'text-base'} ${big ? '' : tone}`} data-testid={testid}>{value}</div>
  </div>
);
